import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DollarSign } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import PrimaryCell from "../../../components/common/PrimaryCell";
import DateRangePicker, { useDatePresetRange } from "../../../components/common/DateRangePicker";
import CustomerPicker from "../../sales/components/CustomerPicker";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { PAYMENT_METHOD_LABELS } from "../../dashboard/collectionsSummaryAggregator";
import { money } from "../../dashboard/dashboardHelpers";
import { useGetCollectionsListPageQuery, useGetCollectionsSummaryQuery } from "../collectionsApi";

const PAGE_SIZE = 100;

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * Collections list.
 *
 * Collections had an API slice and dashboard widgets but no screen of its own -
 * which made it the one module in the One Stop report whose rows had nowhere to
 * drill to. This is that landing screen: read-only, filterable, and able to
 * arrive pre-filtered from a One Stop row via query params
 * (?highlight=<recordGuid>&branchId&dateFrom&dateTo), with the referenced
 * receipt highlighted.
 *
 * Deliberately NOT a full split-view CRUD module: creating and editing
 * collections is the Flutter app's job (field collectors), and inventing a web
 * write path here would duplicate that workflow rather than support this report.
 */
const CollectionsListPage = () => {
  const [searchParams] = useSearchParams();
  const highlight = searchParams.get("highlight");
  const urlBranchId = searchParams.get("branchId");
  const urlFrom = searchParams.get("dateFrom");
  const urlTo = searchParams.get("dateTo");
  const urlCustId = searchParams.get("custId");
  const urlCustName = searchParams.get("custName");

  const dateRange = useDatePresetRange("LAST_30_DAYS");
  const { selectedBranchId } = useBranchFilter();
  const [page, setPage] = useState(1);

  // An incoming ?custId= is SEEDED INTO the picker rather than applied beside
  // it. Applied beside it (the original shape) the list came back narrowed with
  // the Customer box empty, so nothing on screen said why - and worse, clearing
  // the box could not undo it: custId fell back to the URL value, so the filter
  // was stuck for the life of the page. Owning it in one piece of state fixes
  // both. The name is display-only; the filter is still by id.
  const customerFromUrl = (id, name, branch) =>
    id
      ? {
          value: branch ? `${branch}-${id}` : String(id),
          label: name || `Customer ${id}`,
          customer: { custId: Number(id), branchId: branch ? Number(branch) : undefined, custName: name || undefined },
        }
      : null;

  const [customer, setCustomer] = useState(() => customerFromUrl(urlCustId, urlCustName, urlBranchId));

  // A drill-down arrives with its own window; adopt it once so the referenced
  // receipt is actually on screen instead of outside the default 30-day range.
  useEffect(() => {
    if (urlFrom) dateRange.setDateFrom(urlFrom);
    if (urlTo) dateRange.setDateTo(urlTo);
    if (urlFrom || urlTo) dateRange.setPreset("CUSTOM");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFrom, urlTo]);

  // Re-seed when a NEW drill-down lands while this page is already mounted -
  // the useState initializer above only runs on first mount.
  useEffect(() => {
    setCustomer(customerFromUrl(urlCustId, urlCustName, urlBranchId));
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCustId, urlCustName, urlBranchId]);

  const branchId = urlBranchId ? Number(urlBranchId) : selectedBranchId || undefined;
  const custId = customer?.customer?.custId;

  const filterParams = {
    dateFrom: dateRange.dateFrom,
    dateTo: dateRange.dateTo,
    ...(branchId ? { branchId } : {}),
    ...(custId != null ? { custId } : {}),
  };

  // A new period or branch starts again from page 1. Without this, switching
  // from a 30-day window on page 3 to "Today" asked the server for page 3 of a
  // one-page result and the list came back empty - the preset looked broken
  // (2026-09-27, "date picker filters are not behaving properly").
  useEffect(() => {
    setPage(1);
  }, [dateRange.dateFrom, dateRange.dateTo, branchId]);

  const { data, isFetching, error } = useGetCollectionsListPageQuery({ page, pageSize: PAGE_SIZE, ...filterParams });
  // The period total comes from the same proc filters the phone dashboard uses
  // (/collections/summary), not from adding up the 100 rows on screen - the
  // page-sum was being read as the period figure and disagreed with the app.
  const { data: summaryRows } = useGetCollectionsSummaryQuery(filterParams);
  const periodTotal = summaryRows?.[0];

  const rows = data?.items || [];
  const totalCount = periodTotal?.totalCount ?? data?.totalCount ?? rows.length;

  const columns = useMemo(
    () => [
      {
        // The customer leads every list (2026-09-12); the site sits under it.
        title: "Customer",
        dataIndex: "custName",
        render: (v, r) => (
          <PrimaryCell
            primary={v || (r.custId != null ? `cust ${r.custId}` : null)}
            secondary={r.siteLabel || null}
          />
        ),
      },
      {
        title: "Date",
        dataIndex: "colectDate",
        render: (v) => fmtDate(v),
      },
      { title: "Branch", dataIndex: "branchCode" },
      {
        title: "Method",
        dataIndex: "paymentMethod",
        render: (v) => PAYMENT_METHOD_LABELS[v] || "Unspecified",
      },
      { title: "Cheque no.", dataIndex: "chqNumbr", render: (v) => v || "—" },
      { title: "Bank", dataIndex: "bankName", render: (v) => v || "—" },
      { title: "Collected by", dataIndex: "empName", render: (v) => v || "—" },
      {
        title: "Amount",
        dataIndex: "amount",
        align: "right",
        render: (v) => (v == null ? "—" : money(v)),
      },
      {
        title: "Status",
        dataIndex: "isBanked",
        render: (v, r) =>
          r.isDeleted ? (
            <span className="badge bg-secondary-subtle text-secondary">Deleted</span>
          ) : v ? (
            <span className="badge bg-success-subtle text-success">Banked</span>
          ) : (
            <span className="badge bg-warning-subtle text-warning">Not banked</span>
          ),
      },
    ],
    []
  );

  const totalShown = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Collections</h4>
              <h6>Cash and cheque collections recorded in the field</h6>
            </div>
          </div>
        </div>

        {/* Says plainly why the list is narrowed. The wording no longer claims
            "One Stop" specifically - dashboard figures drill in here too. */}
        {highlight && (
          <div className="alert alert-light border d-flex align-items-center gap-2" role="status">
            <DollarSign size={15} className="text-danger flex-shrink-0" />
            <span style={{ fontSize: 13 }}>
              The referenced receipt is highlighted below. Collections has no per-receipt detail
              screen, so this is the list narrowed to that receipt&rsquo;s own day and branch.
            </span>
          </div>
        )}
        {!highlight && customer && urlCustId && (
          <div className="alert alert-light border d-flex align-items-center gap-2" role="status">
            <DollarSign size={15} className="text-danger flex-shrink-0" />
            <span style={{ fontSize: 13 }}>
              Showing <strong>{customer.label}</strong> only
              {urlFrom || urlTo ? ", for the period you came from" : ""}. Clear the Customer box to
              see every collection.
            </span>
          </div>
        )}

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-lg-4">
                <label className="form-label text-muted" style={{ fontSize: 12 }}>
                  Customer
                </label>
                <CustomerPicker branchId={selectedBranchId}
                  isClearable
                  value={customer}
                  onChange={(opt) => {
                    setCustomer(opt);
                    setPage(1);
                  }}
                />
              </div>
              <div className="col-12 col-lg-6">
                <label className="form-label text-muted" style={{ fontSize: 12 }}>
                  Period
                </label>
                <DateRangePicker {...dateRange} />
              </div>
              <div className="col-12 col-lg-2 text-lg-end">
                {/* Header = the WHOLE period (user, 2026-09-27: "the header should show
                    the full amount for that period"); the page's running total is the
                    table footer below, never up here. */}
                <div className="text-muted" style={{ fontSize: 11.5 }}>
                  Period total · {totalCount} record{totalCount === 1 ? "" : "s"}
                </div>
                <div className="fw-bold">{periodTotal ? money(periodTotal.totalAmount) : "…"}</div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            {error?.data?.message || "Could not load collections."}
          </div>
        )}

        <div className="card">
          <div className="card-body">
            <DataTable
              columns={columns}
              dataSource={rows}
              rowKey="recordGuid"
              loading={isFetching}
              onRow={(record) => ({
                className: highlight && record.recordGuid === highlight ? "onestop-highlight-row" : "",
              })}
            />
            {/* Footer: running total of the rows on THIS page only. */}
            {rows.length > 0 && (
              <div className="d-flex justify-content-end align-items-baseline gap-2 border-top pt-2 mt-1">
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Page {page} total ({rows.length} of {totalCount} records)
                </span>
                <span className="fw-semibold">{money(totalShown)}</span>
              </div>
            )}
            {totalCount > rows.length && (
              <div className="d-flex justify-content-between align-items-center mt-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Page {page}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page * PAGE_SIZE >= totalCount || isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollectionsListPage;
